import { createSlice } from "@reduxjs/toolkit";
import {
  createCertificateTemplateVersion,
  getActiveCertificateTemplateVersions,
  getAllCertificateTemplateVersions,
  getCertificateTemplateVersionById,
  updateCertificateTemplateVersion,
} from "./certificateTemplateVersionService";

/* -------------------- Status Enum -------------------- */

export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCEEDED: "succeeded",
  FAILED: "failed",
};

/* -------------------- Initial State -------------------- */

const initialState = {
  createCertificateTemplateVersion: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  certificateTemplateVersionList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
    pageCount: 0,
  },

  updateCertificateTemplateVersion: {
    status: STATUS.IDLE,
    error: null,
    message: null,
  },

  activeCertificateTemplateVersionsList: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },

  certificateTemplateVersionDetails: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },

  certificateTemplateVersionByType: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },

  certificateTemplateVersionByTypeAndEntity: {
    status: STATUS.IDLE,
    error: null,
    data: [],
  },
};

/* -------------------- Slice -------------------- */

const certificateTemplateVersionSlice = createSlice({
  name: "certificateTemplateVersion",
  initialState,
  reducers: {
  resetCertificateTemplateVersionDetails: (state) => {
    state.certificateTemplateVersionDetails.status = STATUS.IDLE;
    state.certificateTemplateVersionDetails.error = null;
    state.certificateTemplateVersionDetails.data = [];
  },
  },
  extraReducers: (builder) => {
    /* ---------- Create Certificate Template ---------- */
    builder
      .addCase(createCertificateTemplateVersion.pending, (state) => {
        state.createCertificateTemplateVersion.status = STATUS.LOADING;
        state.createCertificateTemplateVersion.error = null;
      })
      .addCase(createCertificateTemplateVersion.fulfilled, (state, action) => {
        state.createCertificateTemplateVersion.status = STATUS.SUCCEEDED;
        state.createCertificateTemplateVersion.message = action?.payload;
      })
      .addCase(createCertificateTemplateVersion.rejected, (state, action) => {
        state.createCertificateTemplateVersion.status = STATUS.FAILED;
        state.createCertificateTemplateVersion.error = action?.payload;
      });

    /* ---------- Get Certificate Template List ---------- */
    builder
      .addCase(getAllCertificateTemplateVersions.pending, (state) => {
        state.certificateTemplateVersionList.status = STATUS.LOADING;
        state.certificateTemplateVersionList.error = null;
      })
      .addCase(getAllCertificateTemplateVersions.fulfilled, (state, action) => {
        state.certificateTemplateVersionList.status = STATUS.SUCCEEDED;
        state.certificateTemplateVersionList.data = action?.payload?.data || [];
        state.certificateTemplateVersionList.pageCount = action.payload?.pageCount || 0;
      })
      .addCase(getAllCertificateTemplateVersions.rejected, (state, action) => {
        state.certificateTemplateVersionList.status = STATUS.FAILED;
        state.certificateTemplateVersionList.error = action.payload?.message;
      });

    /* ---------- Update Certificate Template ---------- */
    builder
      .addCase(updateCertificateTemplateVersion.pending, (state) => {
        state.updateCertificateTemplateVersion.status = STATUS.LOADING;
        state.updateCertificateTemplateVersion.error = null;
      })
      .addCase(updateCertificateTemplateVersion.fulfilled, (state, action) => {
        state.updateCertificateTemplateVersion.status = STATUS.SUCCEEDED;
        state.updateCertificateTemplateVersion.message = action?.payload;
      })
      .addCase(updateCertificateTemplateVersion.rejected, (state, action) => {
        state.updateCertificateTemplateVersion.status = STATUS.FAILED;
        state.updateCertificateTemplateVersion.error = action?.payload;
      });

    /* ---------- Get Active Certificate Entity List ---------- */
    builder
      .addCase(getActiveCertificateTemplateVersions.pending, (state) => {
        state.activeCertificateTemplateVersionsList.status = STATUS.LOADING;
        state.activeCertificateTemplateVersionsList.error = null;
      })
      .addCase(getActiveCertificateTemplateVersions.fulfilled, (state, action) => {
        state.activeCertificateTemplateVersionsList.status = STATUS.SUCCEEDED;
        state.activeCertificateTemplateVersionsList.data = action?.payload || [];
      })
      .addCase(getActiveCertificateTemplateVersions.rejected, (state, action) => {
        state.activeCertificateTemplateVersionsList.status = STATUS.FAILED;
        state.activeCertificateTemplateVersionsList.error = action.payload?.message;
      });

    /* ---------- Get Certificate Template by Id ---------- */
    builder
      .addCase(getCertificateTemplateVersionById.pending, (state) => {
        state.certificateTemplateVersionDetails.status = STATUS.LOADING;
        state.certificateTemplateVersionDetails.error = null;
      })
      .addCase(getCertificateTemplateVersionById.fulfilled, (state, action) => {
        state.certificateTemplateVersionDetails.status = STATUS.SUCCEEDED;
        state.certificateTemplateVersionDetails.data = action?.payload || [];
      })
      .addCase(getCertificateTemplateVersionById.rejected, (state, action) => {
        state.certificateTemplateVersionDetails.status = STATUS.FAILED;
        state.certificateTemplateVersionDetails.error = action.payload?.message;
      });
  },
});

/* -------------------- Selectors -------------------- */

export const selectCreateCertificateTemplateVersion = (state) =>
  state.certificateTemplateVersion.createCertificateTemplateVersion;

export const selectCertificateTemplateVersionList = (state) =>
  state.certificateTemplateVersion.certificateTemplateVersionList;

export const selectUpdateCertificateTemplateVersion = (state) =>
  state.certificateTemplateVersion.updateCertificateTemplateVersion;

export const selectActiveCertificateTemplateVersionsList = (state) =>
  state.certificateTemplateVersion.activeCertificateTemplateVersionsList;

export const getCertificateTemplateVersion = (state) =>
  state.certificateTemplateVersion.certificateTemplateVersionDetails;

export const { resetCertificateTemplateVersionDetails } = certificateTemplateVersionSlice.actions;

export default certificateTemplateVersionSlice.reducer;
